<script setup lang="ts">
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useSmsStore } from '@/stores/sms'
import SmsConversationList from '@/components/SmsConversationList.vue'
import SmsConversationMessages from '@/components/SmsConversationMessages.vue'
import SmsNewConversation from '@/components/SmsNewConversation.vue'
import type { Conversation } from '@/types/types'

import type { Lead } from '@/types/types'

const emit = defineEmits<{
  call: [lead: Lead]
}>()

const smsStore = useSmsStore()
const { currentConversation } = storeToRefs(smsStore)

const showNewConversation = ref(false)

const handleSelectConversation = (conversation: Conversation) => {
  smsStore.selectConversation(conversation)
}

const handleBack = () => {
  smsStore.clearCurrentConversation()
}

const handleNewConversation = () => {
  showNewConversation.value = true
}

const handleNewConversationBack = () => {
  showNewConversation.value = false
}

const handleConversationCreated = async (conversationId?: number) => {
  showNewConversation.value = false
  await smsStore.fetchConversations()

  if (!conversationId) {
    // No ID provided, select most recent
    if (smsStore.conversations.length > 0) {
      smsStore.selectConversation(smsStore.conversations[0])
    }
    return
  }

  // Try to find in loaded list first
  let conversation = smsStore.conversations.find((c) => c.id === conversationId)

  // If not in list (e.g., older conversation not in first page), fetch directly
  if (!conversation) {
    conversation = (await smsStore.fetchConversation(conversationId)) ?? undefined
  }

  if (conversation) {
    smsStore.selectConversation(conversation)
  }
}
</script>

<template>
  <div class="bg-white">
    <template v-if="showNewConversation">
      <SmsNewConversation
        @back="handleNewConversationBack"
        @conversation-created="handleConversationCreated"
      />
    </template>

    <template v-else-if="!currentConversation">
      <div class="relative">
        <h3 class="text-center mt-0 mb-[20px]">Messages</h3>
      </div>
      <SmsConversationList
        @select="handleSelectConversation"
        @new-conversation="handleNewConversation"
      />
    </template>

    <template v-else>
      <SmsConversationMessages @back="handleBack" @call="(lead) => emit('call', lead)" />
    </template>
  </div>
</template>
